import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { Loader } from "../components/KpiCard";
import { Modal, Field, TextInput, SelectInput, AddButton } from "../components/Form";
import { Trash2 } from "lucide-react";

const ROLES = ["Chair", "Vice Chair", "Treasurer", "Secretary", "Member"];

export default function Governance() {
  const { user } = useAuth();
  const canManage = ["admin", "finance_officer"].includes(user?.role);
  const [members, setMembers] = useState(null);
  const [resolutions, setResolutions] = useState([]);
  const [mOpen, setMOpen] = useState(false);
  const [rOpen, setROpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mForm, setMForm] = useState({ name: "", board_role: "Member", email: "", committee: "", term_start: "", term_end: "" });
  const [rForm, setRForm] = useState({ title: "", meeting_date: "", resolution_type: "Motion", outcome: "Passed", votes_for: "", votes_against: "" });

  const load = () => { api.get("/board-members").then((r) => setMembers(r.data)); api.get("/resolutions").then((r) => setResolutions(r.data)); };
  useEffect(() => { load(); }, []);

  const addM = async (e) => { e.preventDefault(); setBusy(true);
    try { await api.post("/board-members", mForm); toast.success("Board member added"); setMOpen(false); setMForm({ name: "", board_role: "Member", email: "", committee: "", term_start: "", term_end: "" }); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); } };
  const addR = async (e) => { e.preventDefault(); setBusy(true);
    try { await api.post("/resolutions", { ...rForm, votes_for: Number(rForm.votes_for || 0), votes_against: Number(rForm.votes_against || 0) }); toast.success("Resolution recorded"); setROpen(false); setRForm({ title: "", meeting_date: "", resolution_type: "Motion", outcome: "Passed", votes_for: "", votes_against: "" }); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); } };
  const delM = async (id) => { try { await api.delete(`/board-members/${id}`); toast.success("Removed"); load(); } catch (err) { toast.error(apiError(err)); } };

  if (!members) return <Loader />;

  const mCols = [
    { key: "name", label: "Member", className: "font-medium" },
    { key: "board_role", label: "Role", render: (r) => <StatusBadge tone="blue">{r.board_role}</StatusBadge> },
    { key: "committee", label: "Committee", className: "text-[#5C5C54]" },
    { key: "term_start", label: "Term", render: (r) => `${fmtDate(r.term_start)} – ${fmtDate(r.term_end)}` },
    { key: "status", label: "Status", render: (r) => <StatusBadge tone="green">{r.status}</StatusBadge> },
    { key: "actions", label: "", align: "right", render: (r) => canManage
      ? <button onClick={() => delM(r.id)} data-testid={`del-member-${r.id}`} className="text-stone-400 hover:text-outflow"><Trash2 className="h-4 w-4" /></button> : null },
  ];
  const rCols = [
    { key: "title", label: "Resolution", className: "font-medium" },
    { key: "meeting_date", label: "Meeting", render: (r) => fmtDate(r.meeting_date) },
    { key: "resolution_type", label: "Type", className: "text-[#5C5C54]" },
    { key: "votes", label: "Vote", render: (r) => <span className="font-mono text-sm">{r.votes_for}–{r.votes_against}</span> },
    { key: "outcome", label: "Outcome", render: (r) => <StatusBadge tone={r.outcome === "Passed" ? "green" : r.outcome === "Failed" ? "red" : "amber"}>{r.outcome}</StatusBadge> },
  ];

  return (
    <div>
      <PageHeader title="Board Governance" subtitle="Maintain the board roster and record official resolutions and votes." />

      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-medium text-[#1A1A1A]">Board Roster</h2>
        {canManage && <AddButton onClick={() => setMOpen(true)} label="Add Member" testid="add-member-btn" />}
      </div>
      <DataTable columns={mCols} rows={members} testid="members-table" />

      <div className="flex items-center justify-between mb-3 mt-10">
        <h2 className="text-lg font-medium text-[#1A1A1A]">Resolutions & Minutes</h2>
        {canManage && <AddButton onClick={() => setROpen(true)} label="Record Resolution" testid="add-resolution-btn" />}
      </div>
      <DataTable columns={rCols} rows={resolutions} testid="resolutions-table" />

      <Modal open={mOpen} onOpenChange={setMOpen} title="Add Board Member" onSubmit={addM} busy={busy} testid="member-modal">
        <Field label="Name" span={2}><TextInput required value={mForm.name} onChange={(e) => setMForm({ ...mForm, name: e.target.value })} /></Field>
        <Field label="Role"><SelectInput value={mForm.board_role} onChange={(e) => setMForm({ ...mForm, board_role: e.target.value })} options={ROLES} /></Field>
        <Field label="Committee"><TextInput value={mForm.committee} onChange={(e) => setMForm({ ...mForm, committee: e.target.value })} /></Field>
        <Field label="Term Start"><TextInput type="date" value={mForm.term_start} onChange={(e) => setMForm({ ...mForm, term_start: e.target.value })} /></Field>
        <Field label="Term End"><TextInput type="date" value={mForm.term_end} onChange={(e) => setMForm({ ...mForm, term_end: e.target.value })} /></Field>
      </Modal>
      <Modal open={rOpen} onOpenChange={setROpen} title="Record Resolution" onSubmit={addR} busy={busy} testid="resolution-modal">
        <Field label="Title" span={2}><TextInput required value={rForm.title} onChange={(e) => setRForm({ ...rForm, title: e.target.value })} /></Field>
        <Field label="Meeting Date"><TextInput type="date" value={rForm.meeting_date} onChange={(e) => setRForm({ ...rForm, meeting_date: e.target.value })} /></Field>
        <Field label="Type"><SelectInput value={rForm.resolution_type} onChange={(e) => setRForm({ ...rForm, resolution_type: e.target.value })} options={["Motion", "Budget", "Policy", "Bylaw"]} /></Field>
        <Field label="Outcome"><SelectInput value={rForm.outcome} onChange={(e) => setRForm({ ...rForm, outcome: e.target.value })} options={["Passed", "Failed", "Tabled"]} /></Field>
        <Field label="Votes For / Against">
          <div className="flex gap-2">
            <TextInput type="number" value={rForm.votes_for} onChange={(e) => setRForm({ ...rForm, votes_for: e.target.value })} placeholder="For" />
            <TextInput type="number" value={rForm.votes_against} onChange={(e) => setRForm({ ...rForm, votes_against: e.target.value })} placeholder="Against" />
          </div>
        </Field>
      </Modal>
    </div>
  );
}
